import type {
  TalentProfile,
  TalentListResponse,
  TalentListParams,
  SkillOption,
  ToolOption,
  InterviewRequestPayload,
} from "components/Screens/Marketplace/Customer/types";

import getRequest from "util/APIHelperGet";
import postRequest from "util/APIHelperPost";
import { baseUrl } from "util/APIBaseUrl";

export default async function fetchTalentProfile(talentId: string): Promise<TalentProfile> {
  const { ok, status, payload } = await getRequest(`${baseUrl}/talents/${talentId}/profile/portal`);
  if (!ok) throw new Error(`Failed to fetch talent profile: ${status}`);
  return payload as TalentProfile;
}

export async function fetchTalentList(params?: TalentListParams): Promise<TalentListResponse> {
  const queryParams = new URLSearchParams();
  if (params?.page !== undefined) queryParams.append("page", params.page.toString());
  if (params?.limit !== undefined) queryParams.append("limit", params.limit.toString());
  if (params?.country) queryParams.append("country", params.country);
  if (params?.language) {
    const val = Array.isArray(params.language) ? params.language.join(",") : params.language;
    if (val) queryParams.append("language", val);
  }
  if (params?.skill) {
    const val = Array.isArray(params.skill) ? params.skill.join(",") : params.skill;
    if (val) queryParams.append("skill", val);
  }
  if (params?.tool) {
    const val = Array.isArray(params.tool) ? params.tool.join(",") : params.tool;
    if (val) queryParams.append("tool", val);
  }
  if (params?.customerId) queryParams.append("customerId", params.customerId);

  const qs = queryParams.toString();
  const url = `${baseUrl}/talents/portal${qs ? `?${qs}` : ""}`;

  const { ok, status, payload } = await getRequest(url);
  if (!ok) throw new Error(`Failed to fetch talents: ${status}`);
  return payload as TalentListResponse;
}

export async function fetchSkills(): Promise<SkillOption[]> {
  const { ok, status, payload } = await getRequest(`${baseUrl}/talents/skills`);
  if (!ok) throw new Error(`Failed to fetch skills: ${status}`);
  return payload as SkillOption[];
}

export async function fetchTools(): Promise<ToolOption[]> {
  const { ok, status, payload } = await getRequest(`${baseUrl}/talents/tools`);
  if (!ok) throw new Error(`Failed to fetch tools: ${status}`);
  return payload as ToolOption[];
}

export async function requestInterview(data: InterviewRequestPayload): Promise<any> {
  const { ok, status, payload } = await postRequest(
    `${baseUrl}/talents/interview-request`,
    JSON.stringify(data),
    false
  );
  if (!ok) throw new Error(`Failed to request interview: ${status}`);
  return payload;
}

export async function submitHireRequest(data: Record<string, any>): Promise<any> {
  const { ok, status, payload } = await postRequest(`${baseUrl}/talents/hire-request`, JSON.stringify(data), false);
  if (!ok) throw new Error(`Failed to submit hire request: ${status}`);
  return payload;
}

export async function fetchHireRequests(customerId: string): Promise<any[]> {
  const { ok, payload } = await getRequest(`${baseUrl}/talents/hire-requests?customerId=${customerId}`);
  if (!ok) return [];
  return (payload as any[]) || [];
}

export async function checkInterviewRequested(customerId: string, talentId: string): Promise<boolean> {
  const { ok, payload } = await getRequest(
    `${baseUrl}/talents/interview-check?customerId=${customerId}&talentId=${talentId}`
  );
  if (!ok) return false;
  return (payload as any)?.requested || false;
}
